import { JobCreated, JobStatusResponse, ScanRequest, ScanResponse } from "./types";

const SUBMIT_URL = "/api/scan-async";
const STATUS_BASE = "/api/scan-async";
const TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes total — submit + all polls combined
const POLL_INTERVAL_MS = 2000;
const SUBMIT_RETRY_DELAY_MS = 1000;

let activeController: AbortController | null = null;

export function cancelActiveScan(): void {
  if (activeController) {
    activeController.abort();
    activeController = null;
  }
}

// -- Internal helpers -------------------------------------------------------

/** Promise that resolves after `ms`, or rejects if `signal` aborts first. */
function abortableSleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const timeout = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timeout);
      reject(new DOMException("Aborted", "AbortError"));
    };
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

function humanizeHttpError(status: number, text: string, contentType: string): string {
  const looksLikeJson = contentType.includes("application/json") || text.startsWith("{");
  if (looksLikeJson) {
    try {
      const body = JSON.parse(text);
      if (body.error) return String(body.error);
    } catch {
      // fall through
    }
  }
  if (status === 504 || status === 502) {
    return (
      "Scan service unreachable. Try again in a moment — your last attempt " +
      "may have populated the cache, so a retry could return quickly."
    );
  }
  if (status === 429) return "Rate-limited by upstream chess platform. Try again in a minute.";
  if (status >= 500) return `Upstream error (${status}). Try again in a moment.`;
  if (status === 404) return "Scan no longer available — please submit again.";
  return text || `Request failed (${status})`;
}

async function fetchOnce(
  url: string,
  init: RequestInit,
  signal: AbortSignal,
): Promise<Response> {
  return fetch(url, { ...init, signal });
}

async function fetchWithRetryOnce(url: string, init: RequestInit, signal: AbortSignal): Promise<Response> {
  try {
    return await fetchOnce(url, init, signal);
  } catch (err: unknown) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    await abortableSleep(SUBMIT_RETRY_DELAY_MS, signal);
    return fetchOnce(url, init, signal);
  }
}

// -- Public API -------------------------------------------------------------

export async function submitScan(req: ScanRequest, signal: AbortSignal): Promise<JobCreated> {
  const res = await fetchWithRetryOnce(
    SUBMIT_URL,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(req),
    },
    signal,
  );

  const text = await res.text();
  if (!res.ok) {
    throw new Error(humanizeHttpError(res.status, text, res.headers.get("content-type") || ""));
  }
  return JSON.parse(text) as JobCreated;
}

export async function pollScan(jobId: string, signal: AbortSignal): Promise<JobStatusResponse> {
  const res = await fetchOnce(
    `${STATUS_BASE}/${encodeURIComponent(jobId)}`,
    { method: "GET", headers: { Accept: "application/json" } },
    signal,
  );
  const text = await res.text();
  if (!res.ok) {
    throw new Error(humanizeHttpError(res.status, text, res.headers.get("content-type") || ""));
  }
  return JSON.parse(text) as JobStatusResponse;
}

/**
 * Submit + poll loop. Returns the final ScanResponse on `complete`, throws
 * on `error`. AbortController-driven cancellation propagates to both
 * fetches and the inter-poll sleep.
 */
export async function runScanWithPolling(
  req: ScanRequest,
  onProgress?: (elapsedSeconds: number) => void,
): Promise<ScanResponse> {
  cancelActiveScan();

  const controller = new AbortController();
  activeController = controller;

  // 5-min ceiling across submit + all polls. AbortController surfaces it
  // as the same kind of error path as user-initiated cancellation.
  const timeout = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const submitted = await submitScan(req, controller.signal);

    while (true) {
      if (controller.signal.aborted) {
        throw new DOMException("Aborted", "AbortError");
      }
      const status = await pollScan(submitted.job_id, controller.signal);

      if (status.status === "complete") {
        return status.result;
      }
      if (status.status === "error") {
        throw new Error(status.message);
      }
      // pending
      onProgress?.(status.elapsed_seconds);
      await abortableSleep(POLL_INTERVAL_MS, controller.signal);
    }
  } catch (err: unknown) {
    if (err instanceof DOMException && err.name === "AbortError") {
      throw new Error(
        controller.signal.reason === undefined
          ? "Scan was cancelled."
          : "Scan timed out. Please try again with fewer months.",
      );
    }
    throw err;
  } finally {
    clearTimeout(timeout);
    if (activeController === controller) {
      activeController = null;
    }
  }
}

